import { Divider, Stack, SxProps, Theme, useTheme } from '@mui/material';
import { ComponentType, ReactNode } from 'react';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { IconProps } from '@/components/icons/ThumbsUpIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';

export interface ButtonBarActionItem {
  icon: ComponentType<IconProps>;
  label: string;
  onClick?: () => void;
  iconProps?: Partial<IconProps>;
}

export interface ButtonBarMenuItem {
  /** Accessible name for the overflow button. */
  label: string;
  /** Menu contents. Rendered in an overflow (⋮) button's popover. */
  menu: ReactNode;
  /**
   * Fires when this menu opens or closes. Parents that mount the bar only on
   * hover need this to stay mounted while the menu is open — the menu renders
   * in a portal, so moving the pointer onto it reads as leaving the hover
   * target and would otherwise unmount the bar mid-interaction.
   */
  onMenuOpenChange?: (isOpen: boolean) => void;
}

/** Position in the bar follows array order, menus included. */
export type ButtonBarItem = ButtonBarActionItem | ButtonBarMenuItem;

function isMenuItem(item: ButtonBarItem): item is ButtonBarMenuItem {
  return 'menu' in item;
}

export interface ButtonBarProps {
  items: ButtonBarItem[];
  sx?: SxProps<Theme>;
}

export function ButtonBar({ items, sx }: ButtonBarProps) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      divider={<Divider orientation="vertical" flexItem />}
      onClick={(e) => e.stopPropagation()}
      sx={{
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        borderRadius: 0.5,
        border: `1px solid ${COLORS.GRAY[200]}`,
        boxShadow: theme.palette.shadows.xs,
        overflow: 'clip',
        ...sx,
      }}
    >
      {items.map((item) =>
        isMenuItem(item) ? (
          <ContextMenuButton
            key={item.label}
            size="xs"
            variant="transparent"
            aria-label={item.label}
            onOpen={() => item.onMenuOpenChange?.(true)}
            onClose={() => item.onMenuOpenChange?.(false)}
          >
            {item.menu}
          </ContextMenuButton>
        ) : (
          <Tooltip key={item.label} title={item.label}>
            <IconButton
              size="xs"
              variant="transparent"
              icon={item.icon}
              ariaLabel={item.label}
              onClick={item.onClick}
              iconProps={item.iconProps}
            />
          </Tooltip>
        )
      )}
    </Stack>
  );
}
